import React, { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import clsx from 'clsx';
import type { BagelAiFlowNodeKind } from '../../types/aiFlow';
import styles from './AiFlowTab.module.scss';

export type FlowNodeData = {
  kind: BagelAiFlowNodeKind;
  title: string;
  description: string;
  editable: boolean;
  highlighted: boolean;
  callsLlm?: boolean;
  tools?: string[];
};

const KIND_CLASS: Record<BagelAiFlowNodeKind, string> = {
  input: styles.nodeInput,
  output: styles.nodeOutput,
  gate: styles.nodeGate,
  'human-gate': styles.nodeHumanGate,
  skill: styles.nodeSkill,
  tools: styles.nodeTools,
  prompt: styles.nodeStaticPrompt,
};

function FlowNodeComponent({ data }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const isPill = nodeData.kind === 'input' || nodeData.kind === 'output';

  return (
    <div
      className={clsx(
        styles.flowNode,
        // Every real LLM-call node shares one consistent shape/color regardless
        // of skill — only non-LLM "prompt" nodes (static text, appended-only
        // fragments) fall back to the muted static-content style.
        nodeData.callsLlm ? styles.nodeLlmCall : KIND_CLASS[nodeData.kind],
        isPill && styles.flowNodePill,
        nodeData.highlighted && styles.flowNodeHighlighted,
        styles.flowNodeClickable,
        nodeData.editable && styles.flowNodeEditable,
      )}
      title={nodeData.description}
    >
      <Handle type="target" position={Position.Left} className={styles.handle} />
      <div className={styles.flowNodeTitle}>{nodeData.title}</div>
      {nodeData.tools && nodeData.tools.length > 0 ? (
        <div className={styles.toolChips}>
          {nodeData.tools.map(tool => (
            <span key={tool} className={styles.toolChip}>
              {tool}
            </span>
          ))}
        </div>
      ) : null}
      <Handle type="source" position={Position.Right} className={styles.handle} />
    </div>
  );
}

export default memo(FlowNodeComponent);
